import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ContractDisclaimerDialog,
  ContractPmBlockerDialog,
  useContractGenerationPreflight,
} from "@/components/laboral/ContractGenerationGuard";
import { Navigate, useSearchParams } from "react-router-dom";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { touchSharedSessionActivity } from "@/lib/sessionIdle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  Download,
  FileText,
  FolderOpen,
  HelpCircle,
  Loader2,
  LogOut,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const ALL_PATRONES = "__all__";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function emptyContractForm() {
  return {
    puesto: "",
    salario_diario: "",
    periodicidad_pago: "quincenal",
    tipo_contrato: "indeterminado",
    fecha_inicio_contrato: "",
    fecha_fin_contrato: "",
    fecha_baja: todayIsoDate(),
    jornada: "48 horas semanales",
    horario: "De lunes a viernes",
    actividades: "",
    domicilio_trabajador: "",
    representante_legal: "",
    cargo_representante: "",
    domicilio_patron: "",
  };
}

function StatusIcon({ ok }) {
  return ok
    ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
    : <XCircle className="w-4 h-4 text-slate-300" />;
}

async function downloadBlobResponse(response, fallbackName) {
  const blob = response.data;
  const cd = response.headers["content-disposition"] || "";
  const quoted = cd.match(/filename="([^"]+)"/i);
  const filename = quoted?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

async function downloadPdfResponse(response, fallbackName) {
  return downloadBlobResponse(response, fallbackName);
}

function docsByTipo(documentos, tipos) {
  return (documentos || []).filter((d) => tipos.includes(d.tipo));
}

function nominasPorPeriodo(documentos) {
  const map = {};
  for (const d of documentos || []) {
    if (d.tipo !== "nomina") continue;
    const key = `${d.anio || "—"}-${String(d.mes || 0).padStart(2, "0")}`;
    if (!map[key]) map[key] = [];
    map[key].push(d);
  }
  return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
}

function EstatusLaboralBadge({ estatus, activo }) {
  const esBaja = !activo || String(estatus || "").toLowerCase() === "baja";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
        esBaja ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"
      }`}
    >
      {esBaja ? "Baja" : "Activo"}
    </span>
  );
}

/**
 * Listado de trabajadores con expediente documental (contratos, SAT, nóminas, baja).
 * Reutilizable embebido en Catálogo de trabajadores o pantalla dedicada.
 */
export function TrabajadoresExpedientePanel({
  clientId: clientIdProp,
  setClientId: setClientIdProp,
  clients: clientsProp,
  showClientSelector = true,
  showInstructions = true,
  initialWorkerId = "",
  onGoToAlta,
}) {
  const [clientsInternal, setClientsInternal] = useState([]);
  const [clientIdInternal, setClientIdInternal] = usePersistedClientId(clientsInternal);
  const clients = clientsProp ?? clientsInternal;
  const setClientId = setClientIdProp ?? setClientIdInternal;
  const clientId = clientIdProp ?? clientIdInternal;
  const [config, setConfig] = useState(null);
  const [patronales, setPatronales] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPatron, setFilterPatron] = useState(ALL_PATRONES);
  const [searchQ, setSearchQ] = useState("");
  const [soloIncompletos, setSoloIncompletos] = useState(false);
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("contrato");
  const [busy, setBusy] = useState(false);
  const [contractForm, setContractForm] = useState(emptyContractForm());
  const [completarOpen, setCompletarOpen] = useState(false);
  const uploadRef = useRef(null);
  const [uploadTipo, setUploadTipo] = useState("contrato_firmado");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [fechaBajaById, setFechaBajaById] = useState({});
  const [rowBusyId, setRowBusyId] = useState("");
  const [pmDialogOpen, setPmDialogOpen] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const {
    preflight,
    acepto,
    setAcepto,
    canGenerate,
    refresh: refreshPreflight,
  } = useContractGenerationPreflight({
    clientId,
    trabajadorId: detail?.trabajador?.id || "",
    enabled: Boolean(clientId && detail?.trabajador?.id),
  });

  const instrucciones = config?.instrucciones || "";

  const rowsFiltered = useMemo(() => {
    if (filterEstatus === "activo") {
      return rows.filter((r) => r.activo !== false && String(r.estatus_laboral || "activo").toLowerCase() !== "baja");
    }
    if (filterEstatus === "baja") {
      return rows.filter((r) => r.activo === false || String(r.estatus_laboral || "").toLowerCase() === "baja");
    }
    return rows;
  }, [rows, filterEstatus]);

  const visibleIds = useMemo(() => rowsFiltered.map((r) => r.id).filter(Boolean), [rowsFiltered]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const loadRows = useCallback(async () => {
    if (!clientId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const params = { client_id: clientId, q: searchQ.trim() || undefined };
      if (filterPatron !== ALL_PATRONES) params.registro_patronal_id = filterPatron;
      if (soloIncompletos) params.solo_incompletos = true;
      const { data } = await api.get("/laboral/expedientes-laborales", { params });
      const items = Array.isArray(data.items) ? data.items : [];
      setRows(items);
      setFechaBajaById((prev) => {
        const next = { ...prev };
        for (const row of items) {
          if (!row?.id) continue;
          next[row.id] = row.fecha_baja || prev[row.id] || todayIsoDate();
        }
        return next;
      });
      setSelectedIds([]);
    } catch (e) {
      setRows([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar expedientes");
    } finally {
      setLoading(false);
    }
  }, [clientId, filterPatron, searchQ, soloIncompletos]);

  const loadDetail = useCallback(async (trabajadorId) => {
    if (!clientId || !trabajadorId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/laboral/expedientes-laborales/${trabajadorId}`, {
        params: { client_id: clientId },
      });
      setDetail(data);
      const pre = data.prefill_contrato || {};
      const t = pre.trabajador || data.trabajador?.datos_contractuales || {};
      const p = data.patron || {};
      setContractForm({
        puesto: t.puesto || "",
        salario_diario: t.salario_diario || t.salario_base || t.sdi || "",
        periodicidad_pago: t.periodicidad_pago || "quincenal",
        tipo_contrato: t.tipo_contrato || "indeterminado",
        fecha_inicio_contrato: t.fecha_inicio_contrato || t.fecha_alta_imss || "",
        fecha_fin_contrato: t.fecha_fin_contrato || "",
        jornada: t.jornada || "48 horas semanales",
        horario: t.horario || "De lunes a viernes",
        actividades: t.actividades || "",
        domicilio_trabajador: t.domicilio_trabajador || "",
        representante_legal: p.representante_legal || "",
        cargo_representante: p.cargo_representante || "",
        domicilio_patron: p.domicilio_patron || "",
        fecha_baja: t.fecha_baja || data.trabajador?.datos_contractuales?.fecha_baja || todayIsoDate(),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo abrir el expediente");
    } finally {
      setBusy(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientsProp) return;
    api.get("/clients").then(({ data }) => setClientsInternal(data)).catch(() => setClientsInternal([]));
  }, [clientsProp]);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      setPatronales([]);
      setRows([]);
      setDetail(null);
      return;
    }
    api.get("/laboral/expedientes-laborales/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
    api.get("/laboral/registros-patronales", { params: { client_id: clientId } })
      .then(({ data }) => setPatronales(data.items || []))
      .catch(() => setPatronales([]));
    loadRows();
    setDetail(null);
  }, [clientId, loadRows]);

  useEffect(() => {
    if (!initialWorkerId || !clientId) return;
    openWorker({ id: initialWorkerId });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialWorkerId, clientId]);

  const openWorker = (row) => {
    setDetailTab("contrato");
    loadDetail(row.id);
  };

  const buildGenerarPayload = () => ({
    client_id: clientId,
    registro_patronal_id: detail?.trabajador?.registro_patronal_id || "",
    datos_contractuales: {
      puesto: contractForm.puesto,
      salario_diario: contractForm.salario_diario,
      periodicidad_pago: contractForm.periodicidad_pago,
      tipo_contrato: contractForm.tipo_contrato,
      fecha_inicio_contrato: contractForm.fecha_inicio_contrato,
      fecha_fin_contrato: contractForm.fecha_fin_contrato,
      jornada: contractForm.jornada,
      horario: contractForm.horario,
      actividades: contractForm.actividades,
      domicilio_trabajador: contractForm.domicilio_trabajador,
    },
    patron: {
      representante_legal: contractForm.representante_legal,
      cargo_representante: contractForm.cargo_representante,
      domicilio_patron: contractForm.domicilio_patron,
    },
    acepto_responsabilidad: acepto,
    fecha_baja: contractForm.fecha_baja || todayIsoDate(),
  });

  const onGenerarContrato = async (skipPreflight = false) => {
    if (!detail?.trabajador?.id) return;
    if (!skipPreflight && (detail.campos_faltantes || []).length) {
      setCompletarOpen(true);
      return;
    }
    const pmBlock = (preflight?.blockers || []).find((b) => b.code === "PM_REPRESENTANTE_FALTANTE");
    if (pmBlock) {
      setPmDialogOpen(true);
      return;
    }
    if (preflight?.disclaimer_required && !acepto) {
      setDisclaimerOpen(true);
      return;
    }
    if (!canGenerate && !skipPreflight) {
      await refreshPreflight();
      toast.warning("Complete las validaciones antes de generar el contrato");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(
        `/laboral/expedientes-laborales/${detail.trabajador.id}/contrato/generar`,
        buildGenerarPayload(),
        { responseType: "blob", timeout: 120000 },
      );
      const fname = await downloadPdfResponse(response, "Contrato_Borrador.pdf");
      toast.success(`Contrato generado: ${fname}`);
      setCompletarOpen(false);
      await loadDetail(detail.trabajador.id);
      await loadRows();
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          if (parsed?.detail?.code === "CAMPOS_FALTANTES") {
            setCompletarOpen(true);
            toast.warning(parsed.detail.message || "Complete los datos faltantes");
            return;
          }
          if (parsed?.detail?.code === "PM_REPRESENTANTE_FALTANTE") {
            setPmDialogOpen(true);
            return;
          }
          if (parsed?.detail?.code === "DISCLAIMER_PENDIENTE") {
            setDisclaimerOpen(true);
            return;
          }
          toast.error(parsed.detail?.message || "Error al generar contrato");
          return;
        } catch {
          /* ignore */
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Error al generar contrato");
    } finally {
      setBusy(false);
    }
  };

  const onGenerarRenuncia = async () => {
    if (!detail?.trabajador?.id) return;
    setBusy(true);
    try {
      const response = await api.post(
        `/laboral/expedientes-laborales/${detail.trabajador.id}/renuncia/generar`,
        buildGenerarPayload(),
        { responseType: "blob", timeout: 120000 },
      );
      const fname = await downloadPdfResponse(response, "Renuncia_Voluntaria.pdf");
      toast.success(`Renuncia generada: ${fname}`);
      await loadDetail(detail.trabajador.id);
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al generar renuncia");
    } finally {
      setBusy(false);
    }
  };

  const onSubirDocumento = async (file) => {
    if (!file || !detail?.trabajador?.id) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("tipo", uploadTipo);
      body.append("archivo", file);
      await api.post(
        `/laboral/expedientes-laborales/${detail.trabajador.id}/documentos/subir`,
        body,
        { timeout: 120000 },
      );
      toast.success("Documento cargado al expediente");
      await loadDetail(detail.trabajador.id);
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el documento");
    } finally {
      setBusy(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const onDescargarDoc = async (docId, filename) => {
    if (!detail?.trabajador?.id) return;
    try {
      const response = await api.get(
        `/laboral/expedientes-laborales/${detail.trabajador.id}/documentos/${docId}/descargar`,
        { params: { client_id: clientId }, responseType: "blob" },
      );
      await downloadPdfResponse(response, filename || "documento.pdf");
    } catch (e) {
      toast.error(await detailFromAxiosError(e) || "No se pudo descargar");
    }
  };

  const bulkPayloadBase = (todos) => ({
    client_id: clientId,
    todos,
    trabajador_ids: todos ? [] : selectedIds,
    registro_patronal_id: filterPatron !== ALL_PATRONES ? filterPatron : "",
  });

  const fechasBajaPayload = (ids) => {
    const map = {};
    for (const id of ids) {
      map[id] = fechaBajaById[id] || todayIsoDate();
    }
    return map;
  };

  const persistFechaBaja = async (trabajadorId, fecha) => {
    if (!clientId || !trabajadorId || !fecha) return;
    try {
      await api.put(
        `/laboral/expedientes-laborales/${trabajadorId}/datos-contractuales`,
        { fecha_baja: fecha },
        { params: { client_id: clientId } },
      );
    } catch {
      /* no bloquear generación si falla guardado auxiliar */
    }
  };

  const onFechaBajaChange = (trabajadorId, fecha) => {
    setFechaBajaById((prev) => ({ ...prev, [trabajadorId]: fecha }));
  };

  const onFechaBajaBlur = (trabajadorId) => {
    const fecha = fechaBajaById[trabajadorId];
    if (fecha) persistFechaBaja(trabajadorId, fecha);
  };

  const onGenerarContratosBulk = async (todos) => {
    if (!clientId) return;
    const count = todos ? visibleIds.length : selectedIds.length;
    if (!todos && !count) {
      toast.warning("Seleccione al menos un trabajador");
      return;
    }
    setBulkBusy(true);
    touchSharedSessionActivity();
    try {
      const response = await api.post(
        "/laboral/expedientes-laborales/contratos/generar-masivo",
        { ...bulkPayloadBase(todos), omitir_sin_datos: true },
        { responseType: "blob", timeout: 300000 },
      );
      const fname = await downloadBlobResponse(response, "Contratos_Generados.zip");
      const generados = response.headers["x-generados"] || "?";
      const omitidos = response.headers["x-omitidos"] || "0";
      touchSharedSessionActivity();
      toast.success(`ZIP descargado (${fname}): ${generados} generados, ${omitidos} omitidos por datos incompletos`);
      await loadRows();
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        toast.error((await detailFromAxiosError(e)) || "No autorizado al generar contratos");
        return;
      }
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          toast.error(formatApiError(parsed.detail) || "Error al generar contratos");
          return;
        } catch {
          /* ignore */
        }
      }
      toast.error(
        (await detailFromAxiosError(e))
          || formatApiError(e.response?.data?.detail)
          || "Error al generar contratos en lote",
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const onDescargarContratosBulk = async (todos) => {
    if (!clientId) return;
    const count = todos ? visibleIds.length : selectedIds.length;
    if (!todos && !count) {
      toast.warning("Seleccione al menos un trabajador");
      return;
    }
    setBulkBusy(true);
    try {
      const response = await api.post(
        "/laboral/expedientes-laborales/contratos/descargar-zip",
        { ...bulkPayloadBase(todos), incluir_borradores: true, incluir_firmados: true },
        { responseType: "blob", timeout: 300000 },
      );
      const fname = await downloadBlobResponse(response, "Contratos_Expedientes.zip");
      const incluidos = response.headers["x-incluidos"] || "?";
      toast.success(`Descargados ${incluidos} contrato(s) en ${fname}`);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          if (parsed?.detail?.code === "SIN_CONTRATOS") {
            toast.warning(parsed.detail.message || "Ningún contrato PDF disponible para descargar");
            return;
          }
          toast.error(formatApiError(parsed.detail) || "No se pudo descargar el ZIP");
          return;
        } catch {
          /* ignore */
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar contratos");
    } finally {
      setBulkBusy(false);
    }
  };

  const onGenerarRenunciasBulk = async (todos) => {
    if (!clientId) return;
    const ids = todos ? visibleIds : selectedIds;
    const count = ids.length;
    if (!count) {
      toast.warning("Seleccione al menos un trabajador");
      return;
    }
    setBulkBusy(true);
    try {
      for (const id of ids) {
        await persistFechaBaja(id, fechaBajaById[id] || todayIsoDate());
      }
      const response = await api.post(
        "/laboral/expedientes-laborales/renuncia/generar-masivo",
        {
          ...bulkPayloadBase(todos),
          fechas_baja: fechasBajaPayload(ids),
        },
        { responseType: "blob", timeout: 300000 },
      );
      const fname = await downloadBlobResponse(response, "Renuncias_Generadas.zip");
      const generados = response.headers["x-generados"] || "?";
      const errores = response.headers["x-errores"] || "0";
      toast.success(`ZIP descargado (${fname}): ${generados} renuncias generadas, ${errores} errores`);
      await loadRows();
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          toast.error(formatApiError(parsed.detail) || "Error al generar renuncias");
          return;
        } catch {
          /* ignore */
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Error al generar renuncias en lote");
    } finally {
      setBulkBusy(false);
    }
  };

  const onDescargarRenunciasBulk = async (todos) => {
    if (!clientId) return;
    const count = todos ? visibleIds.length : selectedIds.length;
    if (!todos && !count) {
      toast.warning("Seleccione al menos un trabajador");
      return;
    }
    setBulkBusy(true);
    try {
      const response = await api.post(
        "/laboral/expedientes-laborales/renuncia/descargar-zip",
        { ...bulkPayloadBase(todos), incluir_borradores: true, incluir_firmados: true },
        { responseType: "blob", timeout: 300000 },
      );
      const fname = await downloadBlobResponse(response, "Renuncias_Expedientes.zip");
      const incluidos = response.headers["x-incluidos"] || "?";
      toast.success(`Descargadas ${incluidos} renuncia(s) en ${fname}`);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          if (parsed?.detail?.code === "SIN_RENUNCIAS") {
            toast.warning(parsed.detail.message || "Ninguna renuncia PDF disponible para descargar");
            return;
          }
          toast.error(formatApiError(parsed.detail) || "No se pudo descargar el ZIP");
          return;
        } catch {
          /* ignore */
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar renuncias");
    } finally {
      setBulkBusy(false);
    }
  };

  const onGenerarContratoRapido = async (row) => {
    if (!clientId || !row?.id) return;
    setRowBusyId(row.id);
    try {
      const response = await api.post(
        `/laboral/expedientes-laborales/${row.id}/contrato/generar`,
        {
          client_id: clientId,
          registro_patronal_id: row.registro_patronal_id || "",
        },
        { responseType: "blob", timeout: 120000 },
      );
      const fname = await downloadPdfResponse(response, "Contrato_Borrador.pdf");
      toast.success(`Contrato generado: ${fname}`);
      await loadRows();
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const parsed = JSON.parse(text);
          if (parsed?.detail?.code === "CAMPOS_FALTANTES") {
            toast.warning("Complete datos en el expediente para generar el contrato");
            openWorker(row);
            return;
          }
        } catch {
          /* ignore */
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Error al generar contrato");
    } finally {
      setRowBusyId("");
    }
  };

  const onGenerarRenunciaRapida = async (row) => {
    if (!clientId || !row?.id) return;
    const fecha = fechaBajaById[row.id] || row.fecha_baja || todayIsoDate();
    setRowBusyId(row.id);
    try {
      await persistFechaBaja(row.id, fecha);
      const response = await api.post(
        `/laboral/expedientes-laborales/${row.id}/renuncia/generar`,
        {
          client_id: clientId,
          registro_patronal_id: row.registro_patronal_id || "",
          fecha_baja: fecha,
        },
        { responseType: "blob", timeout: 120000 },
      );
      const fname = await downloadPdfResponse(response, "Renuncia_Voluntaria.pdf");
      toast.success(`Renuncia generada: ${fname}`);
      await loadRows();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al generar renuncia");
    } finally {
      setRowBusyId("");
    }
  };

  const faltantesLabels = useMemo(
    () => (detail?.campos_faltantes || []).map((f) => f.label).join(", "),
    [detail],
  );

  const documentos = detail?.documentos || [];

  return (
    <div className="space-y-4" data-testid="trabajadores-expediente-panel">
      {showInstructions && instrucciones ? (
        <div className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm flex gap-2">
          <HelpCircle className="w-4 h-4 shrink-0 text-sky-700 mt-0.5" />
          <p className="m-0">{instrucciones}</p>
        </div>
      ) : null}

      {clientId ? (
        <section
          className="rounded-lg border-2 border-[#0F2D5C]/20 bg-gradient-to-br from-slate-50 to-white p-4 md:p-5 space-y-4"
          data-testid="panel-acciones-expedientes"
        >
          <div>
            <h3 className="text-sm font-semibold text-[#0F2D5C] m-0">Acciones de contratos y renuncias</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-0">
              {rowsFiltered.length
                ? "Use los botones masivos (automático) o las acciones por fila (manual). Indique la fecha de baja de cada trabajador en la columna correspondiente."
                : "Aún no hay trabajadores en el listado. Regístrelo en Alta o impórtelo desde nómina; luego use estos botones."}
            </p>
            {!rowsFiltered.length && onGoToAlta ? (
              <Button type="button" size="sm" className="mt-2 bg-[#0F2D5C] hover:bg-[#0a2247]" onClick={onGoToAlta}>
                Ir a Alta de trabajadores
              </Button>
            ) : null}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border bg-white p-3 space-y-2">
              <p className="text-xs font-semibold text-[#0F2D5C] m-0">Contratos — automático (selección)</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2247]"
                  size="sm"
                  disabled={bulkBusy || !selectedIds.length || !rowsFiltered.length}
                  onClick={() => onGenerarContratosBulk(false)}
                  data-testid="btn-generar-contratos-seleccion"
                >
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                  Generar contratos ({selectedIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !rowsFiltered.length}
                  onClick={() => onGenerarContratosBulk(true)}
                >
                  Generar todos ({visibleIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !selectedIds.length || !rowsFiltered.length}
                  onClick={() => onDescargarContratosBulk(false)}
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Descargar PDFs ({selectedIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !rowsFiltered.length}
                  onClick={() => onDescargarContratosBulk(true)}
                >
                  Descargar todos
                </Button>
              </div>
            </div>
            <div className="rounded-md border bg-white p-3 space-y-2">
              <p className="text-xs font-semibold text-[#0F2D5C] m-0">Renuncias — automático (fecha por trabajador)</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2247]"
                  size="sm"
                  disabled={bulkBusy || !selectedIds.length || !rowsFiltered.length}
                  onClick={() => onGenerarRenunciasBulk(false)}
                  data-testid="btn-generar-renuncias-seleccion"
                >
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LogOut className="w-3.5 h-3.5 mr-1" />}
                  Generar renuncias ({selectedIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !rowsFiltered.length}
                  onClick={() => onGenerarRenunciasBulk(true)}
                >
                  Generar todas ({visibleIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !selectedIds.length || !rowsFiltered.length}
                  onClick={() => onDescargarRenunciasBulk(false)}
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Descargar renuncias ({selectedIds.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy || !rowsFiltered.length}
                  onClick={() => onDescargarRenunciasBulk(true)}
                >
                  Descargar todas
                </Button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {showClientSelector ? (
            <div className="space-y-1 md:col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Cliente</Label>
              <Select value={clientId || ""} onValueChange={setClientId}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Seleccione cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.razon_social} — {c.rfc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Patrón</Label>
            <Select value={filterPatron} onValueChange={setFilterPatron} disabled={!clientId}>
              <SelectTrigger className="h-10 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PATRONES}>Todos</SelectItem>
                {patronales.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.registro_patronal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Buscar</Label>
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Nombre, CURP, RFC…"
              onKeyDown={(e) => e.key === "Enter" && loadRows()}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button type="button" variant="outline" size="sm" onClick={loadRows} disabled={!clientId || loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Actualizar listado
          </Button>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={soloIncompletos} onChange={(e) => setSoloIncompletos(e.target.checked)} />
            Solo expedientes incompletos
          </label>
          <Select value={filterEstatus} onValueChange={setFilterEstatus} disabled={!clientId}>
            <SelectTrigger className="h-8 w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estatus</SelectItem>
              <SelectItem value="activo">Solo activos</SelectItem>
              <SelectItem value="baja">Solo baja</SelectItem>
            </SelectContent>
          </Select>
          {clientId && rowsFiltered.length ? (
            <span className="text-[10px] text-slate-500 self-center">
              Manual: botones Contrato / Renuncia / Abrir en cada fila
            </span>
          ) : null}
        </div>

        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }}
                    onChange={toggleSelectAll}
                    disabled={!rows.length}
                  />
                </TableHead>
                <TableHead className="text-[10px]">Trabajador</TableHead>
                <TableHead className="text-[10px]">Estatus</TableHead>
                <TableHead className="text-[10px]">Patrón</TableHead>
                <TableHead className="text-[10px] text-center">Contrato</TableHead>
                <TableHead className="text-[10px] text-center">SAT</TableHead>
                <TableHead className="text-[10px] text-center">Nóminas</TableHead>
                <TableHead className="text-[10px] text-center">Baja</TableHead>
                <TableHead className="text-[10px]">Fecha baja</TableHead>
                <TableHead className="text-[10px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowsFiltered.length ? rowsFiltered.map((row) => (
                <TableRow key={row.id} className={selectedIds.includes(row.id) ? "bg-sky-50/50" : undefined}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelectRow(row.id)}
                      aria-label={`Seleccionar ${row.nombre_completo || row.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{row.nombre_completo || "—"}</div>
                    <div className="font-mono text-[10px] text-slate-500">{row.curp || row.rfc || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <EstatusLaboralBadge estatus={row.estatus_laboral} activo={row.activo} />
                  </TableCell>
                  <TableCell className="text-xs font-mono">{row.registro_patronal || "—"}</TableCell>
                  <TableCell className="text-center"><StatusIcon ok={row.tiene_contrato} /></TableCell>
                  <TableCell className="text-center"><StatusIcon ok={row.tiene_sat_carta} /></TableCell>
                  <TableCell className="text-center text-xs">{row.nominas_count || 0}</TableCell>
                  <TableCell className="text-center"><StatusIcon ok={row.tiene_renuncia} /></TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      className="h-8 text-xs w-[130px]"
                      value={fechaBajaById[row.id] || row.fecha_baja || todayIsoDate()}
                      onChange={(e) => onFechaBajaChange(row.id, e.target.value)}
                      onBlur={() => onFechaBajaBlur(row.id)}
                      aria-label={`Fecha de baja ${row.nombre_completo || row.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-[10px] px-2"
                        disabled={rowBusyId === row.id || bulkBusy}
                        onClick={() => onGenerarContratoRapido(row)}
                        data-testid={`btn-contrato-rapido-${row.id}`}
                      >
                        {rowBusyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-0.5" />}
                        Contrato
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-[10px] px-2"
                        disabled={rowBusyId === row.id || bulkBusy}
                        onClick={() => onGenerarRenunciaRapida(row)}
                        data-testid={`btn-renuncia-rapida-${row.id}`}
                      >
                        {rowBusyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3 mr-0.5" />}
                        Renuncia
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="h-8 text-[10px] px-2" onClick={() => openWorker(row)}>
                        <FolderOpen className="w-3 h-3 mr-0.5" />
                        Abrir
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10">
                    <p className="text-sm text-slate-600 m-0 mb-3">
                      {loading ? "Cargando…" : "Sin trabajadores en catálogo para este cliente."}
                    </p>
                    {!loading && onGoToAlta ? (
                      <Button type="button" variant="outline" size="sm" onClick={onGoToAlta}>
                        Registrar o importar en Alta de trabajadores
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#0F2D5C]" />
              Expediente — {detail?.trabajador?.nombre_completo || "Trabajador"}
            </DialogTitle>
          </DialogHeader>

          {detail?.campos_faltantes?.length ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              Para generar contrato faltan: {faltantesLabels}.
            </div>
          ) : null}

          <Tabs value={detailTab} onValueChange={setDetailTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="contrato">Contrato</TabsTrigger>
              <TabsTrigger value="sat">Autorización SAT</TabsTrigger>
              <TabsTrigger value="nominas">Nóminas</TabsTrigger>
              <TabsTrigger value="baja">Renuncia / Baja</TabsTrigger>
            </TabsList>

            <TabsContent value="contrato" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Representante legal (patrón)</Label>
                  <Input value={contractForm.representante_legal} onChange={(e) => setContractForm((p) => ({ ...p, representante_legal: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cargo representante</Label>
                  <Input value={contractForm.cargo_representante} onChange={(e) => setContractForm((p) => ({ ...p, cargo_representante: e.target.value }))} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Domicilio patrón</Label>
                  <Input value={contractForm.domicilio_patron} onChange={(e) => setContractForm((p) => ({ ...p, domicilio_patron: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Puesto</Label>
                  <Input value={contractForm.puesto} onChange={(e) => setContractForm((p) => ({ ...p, puesto: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Salario diario</Label>
                  <Input
                    value={contractForm.salario_diario}
                    onChange={(e) => setContractForm((p) => ({ ...p, salario_diario: e.target.value }))}
                    placeholder="Desde nómina o en blanco al generar"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo contrato</Label>
                  <Select value={contractForm.tipo_contrato} onValueChange={(v) => setContractForm((p) => ({ ...p, tipo_contrato: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(detail?.tipos_contrato || []).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fecha inicio</Label>
                  <Input type="date" value={contractForm.fecha_inicio_contrato} onChange={(e) => setContractForm((p) => ({ ...p, fecha_inicio_contrato: e.target.value }))} />
                </div>
                {contractForm.tipo_contrato === "determinado" ? (
                  <div className="space-y-1">
                    <Label className="text-xs">Fecha fin</Label>
                    <Input type="date" value={contractForm.fecha_fin_contrato} onChange={(e) => setContractForm((p) => ({ ...p, fecha_fin_contrato: e.target.value }))} />
                  </div>
                ) : null}
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Actividades</Label>
                  <Input value={contractForm.actividades} onChange={(e) => setContractForm((p) => ({ ...p, actividades: e.target.value }))} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2247]"
                  disabled={busy || !canGenerate}
                  onClick={() => onGenerarContrato(false)}
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generar contrato PDF
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => { setUploadTipo("contrato_firmado"); uploadRef.current?.click(); }}>
                  <Upload className="w-4 h-4 mr-1" /> Subir contrato firmado
                </Button>
              </div>
              <DocList docs={docsByTipo(documentos, ["contrato_borrador", "contrato_firmado"])} onDownload={onDescargarDoc} empty="Sin contratos en el expediente." />
            </TabsContent>

            <TabsContent value="sat" className="space-y-4 mt-4">
              <p className="text-xs text-slate-600 m-0">
                Genere la carta desde Inscripción SAT o suba aquí la versión firmada.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => { setUploadTipo("sat_firmada"); uploadRef.current?.click(); }}>
                <Upload className="w-4 h-4 mr-1" /> Subir carta SAT
              </Button>
              <DocList docs={docsByTipo(documentos, ["sat_carta", "sat_firmada"])} onDownload={onDescargarDoc} empty="Sin documentos SAT." />
            </TabsContent>

            <TabsContent value="nominas" className="space-y-4 mt-4">
              <Button type="button" variant="outline" size="sm" onClick={() => { setUploadTipo("nomina"); uploadRef.current?.click(); }}>
                <Upload className="w-4 h-4 mr-1" /> Subir nómina PDF
              </Button>
              {nominasPorPeriodo(documentos).length ? nominasPorPeriodo(documentos).map(([periodo, docs]) => (
                <div key={periodo} className="border rounded-md p-2">
                  <p className="text-xs font-medium text-[#0F2D5C] m-0 mb-2">Periodo {periodo}</p>
                  <DocList docs={docs} onDownload={onDescargarDoc} empty="" />
                </div>
              )) : (
                <p className="text-xs text-slate-500">Sin nóminas archivadas.</p>
              )}
            </TabsContent>

            <TabsContent value="baja" className="space-y-4 mt-4">
              <div className="space-y-1 max-w-xs">
                <Label className="text-xs">Fecha de baja (este trabajador)</Label>
                <Input
                  type="date"
                  value={contractForm.fecha_baja || todayIsoDate()}
                  onChange={(e) => setContractForm((p) => ({ ...p, fecha_baja: e.target.value }))}
                />
              </div>
              <Button type="button" variant="outline" disabled={busy} onClick={onGenerarRenuncia}>
                <FileText className="w-4 h-4 mr-2" /> Generar renuncia voluntaria PDF
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setUploadTipo("renuncia_firmada"); uploadRef.current?.click(); }}>
                <Upload className="w-4 h-4 mr-1" /> Subir renuncia firmada
              </Button>
              <DocList docs={docsByTipo(documentos, ["renuncia_borrador", "renuncia_firmada"])} onDownload={onDescargarDoc} empty="Sin documentos de baja." />
            </TabsContent>
          </Tabs>

          <input ref={uploadRef} type="file" accept=".pdf" className="hidden" onChange={(e) => onSubirDocumento(e.target.files?.[0])} />
        </DialogContent>
      </Dialog>

      <Dialog open={completarOpen} onOpenChange={setCompletarOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete datos para el contrato</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            {(detail?.campos_faltantes || []).map((f) => (
              <div key={`${f.grupo}-${f.campo}`} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <Input
                  value={contractForm[f.campo] || ""}
                  onChange={(e) => setContractForm((p) => ({ ...p, [f.campo]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletarOpen(false)}>Cancelar</Button>
            <Button className="bg-[#0F2D5C]" disabled={busy || !canGenerate} onClick={() => onGenerarContrato(true)}>
              Generar contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContractPmBlockerDialog
        open={pmDialogOpen}
        onOpenChange={setPmDialogOpen}
        message={preflight?.blockers?.find((b) => b.code === "PM_REPRESENTANTE_FALTANTE")?.message}
      />
      <ContractDisclaimerDialog
        open={disclaimerOpen}
        onOpenChange={setDisclaimerOpen}
        disclaimerText={preflight?.disclaimer_text}
        onAccept={() => {
          setAcepto(true);
          refreshPreflight();
        }}
      />
    </div>
  );
}

/** Ruta legada: redirige al catálogo con la pestaña de trabajadores registrados. */
export default function ExpedientesLaborales() {
  const [searchParams] = useSearchParams();
  const open = searchParams.get("open");
  const target = open
    ? `/catalogo-trabajadores?tab=registrados&open=${encodeURIComponent(open)}`
    : "/catalogo-trabajadores?tab=registrados";
  return <Navigate to={target} replace />;
}

function DocList({ docs, onDownload, empty }) {
  if (!docs?.length) {
    return empty ? <p className="text-xs text-slate-500">{empty}</p> : null;
  }
  return (
    <ul className="space-y-1 m-0 p-0 list-none">
      {docs.map((d) => (
        <li key={d.id} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1.5">
          <span className="truncate">{d.filename}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{d.tipo}</span>
          {d.file_path ? (
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => onDownload(d.id, d.filename)}>
              <Download className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <span className="text-[10px] text-amber-700">Sin archivo en disco</span>
          )}
        </li>
      ))}
    </ul>
  );
}
